/**
 * @file DesktopLayout.jsx
 * @description Masaüstü fullscreen layout bileşeni
 *
 * Yan yana liste ve harita görünümü sunar. Sol panelde
 * filtreler ve ilan listesi, sağ panelde harita ve
 * floating detay kartı yer alır.
 *
 * @module widget/components/layouts/DesktopLayout
 */

import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { X } from "lucide-react";
import { formatNumber, openExternal } from "../../utils";
import { getThemeClass, getThemeStyle } from "../../utils/theme";
import { FiltersSidebar } from "../FiltersSidebar";
import { CompactListingCard } from "../CompactListingCard";
import { MapPane } from "../MapPane";
import { MapMarkerPopup } from "../MapMarkerPopup";
import { MapEmptyState } from "../MapEmptyState";
import { ListingDetailView } from "../ListingDetailView";
import { TIMEOUTS } from "../../utils/constants";

/**
 * Detay paneli stil hesaplayıcısı
 *
 * @param {boolean} isDark - Karanlık mod aktif mi
 * @returns {Object} CSS stil objesi
 */
const getDetailPanelStyle = (isDark) => ({
  backgroundColor: getThemeStyle.bgPrimary(isDark),
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
});

/**
 * Masaüstü Layout Bileşeni
 *
 * Geniş ekranlar için optimize edilmiş iki panelli layout.
 * TripAdvisor tarzı floating detay kartı ile harita
 * üzerinde detay gösterimi yapar.
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {Object[]} props.listings - İlan listesi
 * @param {Object} props.filters - Aktif filtreler
 * @param {number} props.total - Toplam sonuç
 * @param {boolean} props.isDark - Karanlık mod
 * @param {string|number} props.containerHeight - Container yüksekliği
 * @param {Function} props.onRemoveFilter - Filtre kaldırma
 * @param {Function} props.onApplyFilter - Filtre uygulama
 * @param {Function} props.onAddFilter - Filtre ekleme
 * @param {Function} props.onDetailClick - Detay açma
 * @param {Function} props.onExitFullscreen - Fullscreen'den çıkış
 * @param {number|null} props.selectedListingId - Seçili ilan
 * @param {Object|null} props.listingDetail - İlan detay verisi
 * @param {boolean} props.detailLoading - Detay yükleniyor
 * @param {number|null} props.regionAveragePrice - Bölge fiyat ortalaması
 * @param {number} props.selectedImageIndex - Seçili görsel indeksi
 * @param {Function} props.setSelectedImageIndex - Görsel seçim setter
 * @param {Function} props.onCloseDetail - Detay kapatma
 * @param {Function} props.onMapSearchInArea - Harita viewport araması
 * @param {boolean} props.isMapSearching - Arama devam ediyor
 * @param {boolean} [props.mapSearchEmpty=false] - Boş sonuç durumu
 * @param {Set<string>} [props.savedListingIdSet] - Kaydedilen ilan ID'leri Set'i
 * @param {Function} [props.onToggleSave] - Kaydetme toggle callback'i
 * @param {boolean} [props.isSaveAtLimit] - Kaydetme limiti doldu mu
 * @param {Set<string>} [props.compareListingIdSet] - Karşılaştırmadaki ilan ID'leri Set'i
 * @param {Function} [props.onToggleCompare] - Karşılaştırma toggle callback'i
 * @param {boolean} [props.isCompareAtLimit] - Karşılaştırma limiti doldu mu
 *
 * @returns {JSX.Element} Layout bileşeni
 *
 * @example
 * <DesktopLayout
 *   listings={listings}
 *   filters={filters}
 *   total={150}
 *   isDark={false}
 *   containerHeight="100vh"
 *   onDetailClick={(id) => fetchDetail(id)}
 *   {...otherProps}
 * />
 *
 * @remarks
 * - Sol panel sabit 420px genişliğinde
 * - Sağ panel kalan alanı kaplar
 * - Detay kartı harita üzerinde floating olarak gösterilir
 * - Marker popup'ı detay kartından bağımsız çalışır
 */
export function DesktopLayout({
  listings,
  filters,
  total,
  isDark,
  containerHeight,
  onRemoveFilter,
  onApplyFilter,
  onAddFilter,
  onDetailClick,
  onExitFullscreen,
  selectedListingId,
  listingDetail,
  detailLoading,
  regionAveragePrice,
  selectedImageIndex,
  setSelectedImageIndex,
  onCloseDetail,
  onMapSearchInArea,
  isMapSearching,
  mapSearchEmpty = false,
  // Save & Compare props
  savedListingIdSet,
  onToggleSave,
  isSaveAtLimit = false,
  compareListingIdSet,
  onToggleCompare,
  isCompareAtLimit = false,
}) {
  const [markerPopupListing, setMarkerPopupListing] = useState(null);
  const mapContainerRef = useRef(null);
  const [mapDimensions, setMapDimensions] = useState({ width: 800, height: 600 });

  const handleMarkerSelect = useCallback((listing) => {
    setMarkerPopupListing(listing);
  }, []);

  useEffect(() => {
    const updateDimensions = () => {
      if (mapContainerRef.current) {
        const rect = mapContainerRef.current.getBoundingClientRect();
        setMapDimensions({ width: rect.width, height: rect.height });
      }
    };
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    const timer = setTimeout(updateDimensions, TIMEOUTS.FIT_MAP_DELAY);
    return () => {
      window.removeEventListener("resize", updateDimensions);
      clearTimeout(timer);
    };
  }, [containerHeight]);

  // Memoized callbacks
  const handleMapMove = useCallback(() => setMarkerPopupListing(null), []);
  const handleClosePopup = useCallback(() => setMarkerPopupListing(null), []);
  const handleViewDetails = useCallback((id) => {
    setMarkerPopupListing(null);
    if (id) onDetailClick(id);
  }, [onDetailClick]);

  // Memoized detail panel style
  const detailPanelStyle = useMemo(() => getDetailPanelStyle(isDark), [isDark]);

  // Memoized listings rendering
  const renderedListings = useMemo(() =>
    listings.map((listing, index) => {
      const listingId = typeof listing?.id === "number" && Number.isFinite(listing.id) ? listing.id : null;
      const listingIdStr = listingId ? String(listingId) : null;
      const isSaved = listingIdStr && savedListingIdSet ? savedListingIdSet.has(listingIdStr) : false;
      const isInCompare = listingIdStr && compareListingIdSet ? compareListingIdSet.has(listingIdStr) : false;
      return (
        <CompactListingCard
          key={listingId ?? listing?.url ?? `listing-${index}`}
          listing={listing}
          onDetailClick={onDetailClick}
          isDark={isDark}
          openExternal={openExternal}
          isSaved={isSaved}
          onToggleSave={onToggleSave}
          canSave={!isSaveAtLimit || isSaved}
          isInCompare={isInCompare}
          onToggleCompare={onToggleCompare}
          canCompare={!isCompareAtLimit || isInCompare}
        />
      );
    }), [listings, onDetailClick, isDark, savedListingIdSet, onToggleSave, isSaveAtLimit, compareListingIdSet, onToggleCompare, isCompareAtLimit]);

  return (
    <div className="flex flex-1 min-h-0 flex-row">
      {/* Left Panel - Listings */}
      <div
        className={`w-[420px] flex-shrink-0 flex flex-col border-r ${getThemeClass.border(isDark)} ${getThemeClass.bgPrimary(isDark)}`}
      >
        {/* Header - Result count */}
        <div className={`flex items-center px-4 py-3 border-b flex-shrink-0 ${getThemeClass.border(isDark)}`}>
          <div className={`text-sm font-medium ${getThemeClass.textPrimary(isDark)}`}>
            {total ? `${formatNumber(total)} sonuç` : `${listings?.length || 0} sonuç`}
          </div>
        </div>

        {/* Filters */}
        <div className="flex-shrink-0">
          <FiltersSidebar
            filters={filters}
            total={total}
            onRemoveFilter={onRemoveFilter}
            onApplyFilter={onApplyFilter}
            onAddFilter={onAddFilter}
            isDark={isDark}
          />
        </div>

        {/* Listings scroll area */}
        <div
          className="flex-1 min-h-0 overflow-y-auto"
          style={{ overscrollBehavior: 'contain' }}
        >
          {mapSearchEmpty ? (
            <MapEmptyState isDark={isDark} variant="centered" />
          ) : (
            renderedListings
          )}
        </div>
      </div>

      {/* Right Panel - Map (independent section) */}
      <div
        ref={mapContainerRef}
        className={`flex-1 min-h-0 relative overflow-hidden ${getThemeClass.bgTertiary(isDark)}`}
      >
        {/* Map fills the right panel */}
        <MapPane
          listings={listings}
          isDark={isDark}
          fullHeight={true}
          selectedListingId={selectedListingId}
          onSelectListing={handleMarkerSelect}
          onMapMove={handleMapMove}
          onSearchInArea={onMapSearchInArea}
          showSearchButton={!!onMapSearchInArea && !isMapSearching}
        />

        {/* Marker popup */}
        {markerPopupListing && (
          <MapMarkerPopup
            listing={markerPopupListing}
            onClose={handleClosePopup}
            onViewDetails={handleViewDetails}
            openExternal={openExternal}
            isDark={isDark}
            containerHeight={mapDimensions.height}
            containerWidth={mapDimensions.width}
          />
        )}

        {/* Detail Panel - Floating card on map (TripAdvisor style) */}
        {selectedListingId && (
          <div
            className="absolute right-4 top-4 w-[360px] max-h-[calc(100%-32px)] z-20 rounded-2xl overflow-hidden overflow-y-auto shadow-2xl"
            style={detailPanelStyle}
          >
            {/* Close button */}
            <button
              onClick={onCloseDetail}
              aria-label="Detayları kapat"
              className={`absolute top-3 right-3 z-30 h-8 w-8 rounded-full flex items-center justify-center transition-colors ${getThemeClass.closeButtonOverlay(isDark)}`}
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
            <ListingDetailView
              detail={listingDetail}
              loading={detailLoading}
              regionAveragePrice={regionAveragePrice}
              selectedImageIndex={selectedImageIndex}
              setSelectedImageIndex={setSelectedImageIndex}
              isDark={isDark}
              isFloatingCard={true}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default DesktopLayout;
